'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, History, Minus, Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useMediaQuery } from '@/lib/hooks/use-media-query';
import { cn } from '@/lib/utils';

import { ASSISTANT_BUBBLE_ID } from './assistant-bubble';
import { useAssistant } from './assistant-provider';

/**
 * The chat surface: a floating card on desktop, a full-height sheet below `md`.
 *
 * Deliberately NOT a modal. `aria-modal="false"` and no focus trap, because the
 * whole point is that a user clicks a link in a reply and keeps working while
 * the conversation stays where they left it. Escape minimizes rather than
 * closes, so nothing is lost by reflex, and focus returns to the launcher
 * bubble whenever the overlay leaves the open state (minimize, close, or the
 * ⌘/Ctrl+J shortcut) so keyboard users never lose their place.
 */
export function AssistantOverlay({ children }: { children: ReactNode }) {
  const { overlay, minimize, close, toggle, visible, showHistory, setShowHistory, newConversation } =
    useAssistant();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const wasOpenRef = useRef(false);
  const [tall, setTall] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        toggle();
        return;
      }
      if (event.key === 'Escape' && overlay === 'open') {
        event.preventDefault();
        minimize();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [minimize, overlay, toggle, visible]);

  // Focus restoration: remember that we were open, and once we are no longer
  // open, hand focus back to the bubble (which has just remounted, since it
  // renders null while the overlay is open). Runs every render regardless of
  // the early return below, since hooks must stay unconditional.
  useEffect(() => {
    if (overlay === 'open') {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      document.getElementById(ASSISTANT_BUBBLE_ID)?.focus();
    }
  }, [overlay]);

  if (!visible || overlay !== 'open') return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="rii assistant"
      className={cn(
        'fixed z-40 flex flex-col overflow-hidden border bg-background shadow-xl',
        isDesktop
          ? cn('bottom-6 right-6 w-[420px] rounded-xl', tall ? 'h-[85vh]' : 'h-[70vh]')
          : 'inset-x-0 bottom-0 top-0 rounded-none',
      )}
    >
      <header className="flex h-12 shrink-0 items-center justify-between border-b px-3">
        <span className="text-sm font-medium">Assistant</span>
        <div className="flex items-center gap-1">
          {isDesktop && (
            <Button
              variant="ghost"
              size="sm"
              className="size-8 p-0"
              aria-label={tall ? 'Shrink the assistant' : 'Expand the assistant'}
              onClick={() => setTall((current) => !current)}
            >
              {tall ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="size-8 p-0"
            aria-label={showHistory ? 'Back to the conversation' : 'Conversation history'}
            onClick={() => setShowHistory(!showHistory)}
          >
            <History className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="size-8 p-0"
            aria-label="New chat"
            onClick={newConversation}
          >
            <Plus className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="size-8 p-0"
            aria-label="Minimize the assistant"
            onClick={minimize}
          >
            <Minus className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="size-8 p-0"
            aria-label="Close the assistant"
            onClick={close}
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}
